// ============================================================
// ANSICHTSSACHE-AGENT – Laeuft der Agent in der App „Agent“?
// ============================================================
// Seit dem 20.10.2026 gibt es die App „Agent“ fuer das iPhone
// (`native/apple`); sie loest die PWA ab. Ihre Oberflaeche ist dieser
// Agent in einer Web-Ansicht, und die meldet sich in der Kennung des
// Browsers mit „AgentApp/iOS/<Version>“. Daran haengt:
//
//   - kein Hinweis „Zum Home-Bildschirm“ — die App IST installiert
//     (`components/pwa/installation.ts`)
//   - Knoepfe fuer das, was nur die App kann, ueber ihr eigenes
//     Schema `ansichtssache-agent://` — etwa „In der App einrichten“
//     beim Stempelchip
//   - Mein Konto › App: ist die App schon verbunden, setzt sie das
//     Cookie `agent_app_verbunden=<Anfang ihres Schluessels>`; passt es
//     zu einem nicht getrennten Geraet der Person, steht dort
//     „Diese App ist verbunden“ statt der Karte zum Verbinden
//
// Rein und ohne Abhaengigkeiten: Browser und Server lesen dieselbe
// Regel (Server aus dem Kopf `user-agent`).
// ============================================================

/** Die Marke der App in der Kennung des Browsers. */
export const NATIVE_APP_MARKER = "AgentApp/";

/** Das eigene Schema der Apps (Verbinden, native Fenster). */
export const NATIVE_APP_SCHEME = "ansichtssache-agent";

export function isNativeApp(userAgent: string | null | undefined): boolean {
  return !!userAgent && userAgent.includes(NATIVE_APP_MARKER);
}

/** Ein Weg in ein natives Fenster der App, z. B. `stempelchip`. */
export function nativeAppLink(ziel: string): string {
  return `${NATIVE_APP_SCHEME}://${ziel}`;
}

/** Setzt die App, sobald sie verbunden ist; Wert: Anfang ihres Schluessels. */
export const APP_CONNECTED_COOKIE = "agent_app_verbunden";

/**
 * Welches der verbundenen Geraete ist diese App? Nur ein exakter Treffer
 * auf den Schluesselanfang eines nicht getrennten Geraets zaehlt — ein
 * altes Cookie nach dem Trennen zeigt wieder den Weg zum Verbinden.
 */
export function connectedAppDevice<T extends { id: string; keyPrefix: string }>(
  devices: T[],
  cookieValue: string | null | undefined,
): T | null {
  const prefix = cookieValue?.trim();
  if (!prefix) return null;
  return devices.find((device) => device.keyPrefix === prefix) ?? null;
}

/** Auf welchem Geraet die App laeuft — fuer ihren Namen in der Liste. */
export type AppPlatform = "iPhone" | "iPad" | "Mac";

/** So heisst die App in der Geraeteliste, je Geraet (`connectThisApp`). */
export const APP_DEVICE_NAME: Record<AppPlatform, string> = {
  iPhone: "App „Agent“ (iPhone)",
  iPad: "App „Agent“ (iPad)",
  Mac: "App „Agent“ (Mac)",
};

/**
 * Das Geraet der App aus der Kennung ihrer Web-Ansicht. Die iPad-Ansicht
 * gibt sich seit iPadOS 13 als Mac aus („Macintosh“) — mit `AgentApp/iOS`
 * ist sie trotzdem ein iPad.
 */
export function appPlatform(userAgent: string | null | undefined): AppPlatform | null {
  if (!userAgent || !isNativeApp(userAgent)) return null;
  if (userAgent.includes(`${NATIVE_APP_MARKER}macOS`)) return "Mac";
  if (/iPad|Macintosh/.test(userAgent)) return "iPad";
  return "iPhone";
}

/** Rueckweg vom Namen in der Liste zum Geraet; fremde Namen: `null`. */
export function appPlatformOfName(name: string): AppPlatform | null {
  const treffer = (Object.keys(APP_DEVICE_NAME) as AppPlatform[]).find(
    (platform) => APP_DEVICE_NAME[platform] === name,
  );
  return treffer ?? null;
}

/** Wie `lib/push/devices.ts › geraeteName` das System einer PWA nennt. */
const PWA_SYSTEM: Record<AppPlatform, string> = { iPhone: "iPhone", iPad: "iPad", Mac: "macOS" };

/**
 * Kaeme eine Meldung an diese PWA wohl doppelt, weil auf demselben
 * Geraetetyp die App Meldungen ueber Apple bekommt? Nur ein Verdacht:
 * „dasselbe Geraet“ verraet die Kennung nicht (zwei iPhones), und ein
 * iPad-Safari heisst „auf macOS“. Deshalb ein Hinweis mit Rueckfrage,
 * nie ein stilles Auslassen (Mein Konto › Benachrichtigungen).
 */
export function pwaDoubledByApp(
  pwaLabel: string | null | undefined,
  appPlatforms: readonly AppPlatform[],
): AppPlatform | null {
  if (!pwaLabel) return null;
  return appPlatforms.find((platform) => pwaLabel.endsWith(` auf ${PWA_SYSTEM[platform]}`)) ?? null;
}
